import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useMerchantPortalAxios from "../../utils/axios";
import PayrollAPI from "../../api/PayrollAPI";
import { setFeatureData, setTableData } from "../store/slices/appSlice";
import { SESSION_STORAGE_KEYS } from "../constants/sessionStorage";
import { normalizeLastId } from "../utils/loadMore";

// New-folder service entry point for Payroll.
// Keeps old API payload/business logic while adapting navigation to New routes.
export default function usePayrollService() {
  const legacy = PayrollAPI();
  const client = useMerchantPortalAxios();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const hasActiveSession = () =>
    Boolean(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID) &&
        sessionStorage.getItem(SESSION_STORAGE_KEYS.TOKEN),
    );

  const GetPayrollSummaryByReferenceNo = useCallback(
    async (reference) => {
      if (!hasActiveSession()) return null;

      const response = await client.post(
        "/v1/Payroll/GetPayrollSummaryByReferenceNo",
        {
          SessionID: sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID),
          ReferenceNo: reference,
        },
      );

      const payload = response?.data?.Payload || {};
      dispatch(
        setFeatureData({
          section: "details",
          key: "payrollSummary",
          value: {
            payrollCheck: true,
            payrollSummary: payload,
            referenceNo: reference,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "summary",
          key: "payrollSummary",
          value: payload?.PayrollSummary || {},
        }),
      );
      dispatch(
        setTableData({
          key: "payrollSummary",
          data: Array.isArray(payload?.PayrollInfoList) ? payload.PayrollInfoList : [],
        }),
      );
      navigate("/pendingapprovals/pendingsummary");
      return payload;
    },
    [client, dispatch, navigate],
  );

  const GetPayrollSubmitted = useCallback(
    async (searchInput, currency, status, options = {}) => {
      if (!hasActiveSession()) {
        return [];
      }

      const requestPayload = {
        SessionID: sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID),
        SearchInput: searchInput,
        Currency: currency,
        Status: status,
        LastId: normalizeLastId(options?.lastId),
      };

      if (options?.dateFrom) requestPayload.DateFrom = options.dateFrom;
      if (options?.dateTo) requestPayload.DateTo = options.dateTo;

      const response = await client.post(
        "/v1/Payroll/GetPayrollSubmitted",
        requestPayload,
      );

      const payrolls = response?.data?.Payload?.payrolls || [];

      return payrolls;
    },
    [client],
  );

  const GetPayrollOverview = useCallback(
    async (
      lastId,
      input,
      currency,
      status,
      payrollId,
      isExport = false,
    ) => {
      if (!hasActiveSession()) return [];

      const response = await client.post("/v1/Payroll/GetPayrollOverview", {
        SessionID: sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID),
        LastID: normalizeLastId(lastId),
        SearchInput: input || "",
        Currency: currency,
        Status: status,
        PayrollReferenceNumber: payrollId,
        IsExport: isExport,
      });

      return response?.data?.Payload?.PayrollSOAs || [];
    },
    [client],
  );

  return useMemo(
    () => ({
      ...legacy,
      GetPayrollOverview,
      GetPayrollSummaryByReferenceNo,
      GetPayrollSubmitted,
    }),
    [
      legacy,
      GetPayrollOverview,
      GetPayrollSummaryByReferenceNo,
      GetPayrollSubmitted,
    ],
  );
}
